import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { createClient } from '@supabase/supabase-js';
import './Login.css'; // Reuse login page styling

// Create a fresh supabase client without any cached auth
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

function RequestAccess() {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    company: '',
    reason: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Create a fresh Supabase client without any cached session
      const freshClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: false, // Don't persist any session
          autoRefreshToken: false,
        }
      });

      console.log('Submitting with fresh anon client...');

      // Insert access request into database
      const { data, error } = await freshClient
        .from('access_requests')
        .insert([formData])
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('An access request with this email already exists.');
        }
        throw error;
      }

      console.log('Insert successful:', data);

      // Call Edge Function to notify admin
      try {
        await fetch(`${supabaseUrl}/functions/v1/notify-access-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({ record: data }),
        });
      } catch (notifyError) {
        // Don't fail the request if notification fails
        console.error('Failed to send notification:', notifyError);
      }

      setSubmitStatus('success');
      setFormData({
        email: '',
        full_name: '',
        company: '',
        reason: '',
      });
    } catch (error) {
      console.error('Error submitting access request:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit access request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center mb-4">
          <h1 className="text-white mb-2">ROE Visual DB</h1>
          <p className="text-white-50">Production Barcode Checker</p>
        </div>

        <Card style={{ maxWidth: '600px', width: '100%' }} className="shadow-lg">
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h2>Request Access</h2>
              <p className="text-muted">
                Fill out the form below to request access
              </p>
            </div>

          {submitStatus === 'success' && (
            <Alert variant="success" onClose={() => setSubmitStatus(null)} dismissible>
              <Alert.Heading>Request Submitted!</Alert.Heading>
              <p>
                Your access request has been submitted successfully. You will receive an email
                once your request has been reviewed by our team.
              </p>
            </Alert>
          )}

          {submitStatus === 'error' && (
            <Alert variant="danger" onClose={() => setSubmitStatus(null)} dismissible>
              <Alert.Heading>Error</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                You'll receive access confirmation at this email address.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company</Form.Label>
              <Form.Control
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Enter your company name"
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Reason for Access</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Briefly describe why you need access"
                disabled={isSubmitting}
              />
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </Form>

          <div className="text-center mt-4">
            <small className="text-muted">
              Already have an account? <a href="/login">Sign in here</a>
            </small>
          </div>
        </Card.Body>
      </Card>
    </Container>
    </div>
  );
}

export default RequestAccess;
